import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { IMemberRow } from './Columns';
import CoachArchiveNewModal from '../CoachesTable/CoachArchiveModal/CoachArchiveModal';
import AddTeamMemberModal from '../AddTeamMemberModal/AddTeamMemberModal';
import { FormValues } from '../../utils/validations/memberDetailsForm';
import {
  closeAddMemberModalOnSuccess,
  editTeamMemberDataSuccess,
  Role,
} from '../../redux/coachGroup/coachGroupSlice';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

interface IRowActionsProps {
  row: IMemberRow;
  onSuccess: () => void;
}

interface ICustomMenuItemProps {
  icon: string;
  text: string;
  onClick: () => void;
}

function CustomMenuItem({ icon, text, onClick }: ICustomMenuItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      sx={{
        fontFamily: 'Satoshi',
        fontSize: 17,
        fontWeight: 400,
        lineHeight: '22px',
        letterSpacing: '-0.40799999237060547px',
        textAlign: 'left',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <img src={icon} alt="icon" style={{ marginRight: 8, width: 24, height: 24 }} />
        <Typography variant="body1">{text}</Typography>
      </Box>
    </MenuItem>
  );
}

export default function TeamMemberDropDownMenu({ row, onSuccess }: IRowActionsProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showModal, setShowModal] = useState<'EDIT' | 'ARCHIVE' | undefined>(undefined);
  const dispatch = useAppDispatch();
  const [editMemberData, setEditMemberData] = useState<Partial<FormValues> | null>(null);
  const editMemberDataSuccess = useAppSelector((state) => state.coachGroup.isAddMemberSuccess);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string) => {
    if (value === 'archived') {
      setShowModal('ARCHIVE');
      handleClose();
    } else {
      setEditMemberData({
        id: row.id,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email,
        phoneNumber: row.tp,
        role: row.role,
      });
      setShowModal('EDIT');
      handleClose();
    }
  };

  const handleDismiss = () => {
    setShowModal(undefined);
  };

  const handleSuccess = () => {
    setShowModal(undefined);
    onSuccess();
  };

  const handleOnEdit = () => {
    setShowModal(undefined);
  };

  useEffect(() => {
    if (editMemberDataSuccess) {
      handleOnEdit();
      onSuccess();
      dispatch(closeAddMemberModalOnSuccess());
    }
  }, [editTeamMemberDataSuccess]);

  if (row.role === Role.PRINCIPAL_COACH) {
    return null;
  }

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <MoreHorizIcon />
      </IconButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {checkPermissions(UserPermissionType.TEAM_MODULE_EDIT_TEAM_MEMBER) && (
          <CustomMenuItem
            icon="assets/icons/edit-pen.svg"
            text={t('teamMembersTable.actions.editMember')}
            onClick={() => handleMenuItemClick(`edit`)}
          />
        )}
        {checkPermissions(UserPermissionType.TEAM_MODULE_ARCHIVE_TEAM_MEMBER) && (
          <CustomMenuItem
            icon="assets/icons/archive-blue.svg"
            text={t('teamMembersTable.actions.archiveMember')}
            onClick={() => handleMenuItemClick(`archived`)}
          />
        )}
      </Menu>
      {showModal === 'ARCHIVE' && (
        <CoachArchiveNewModal
          coachId={row.id}
          isOpen={showModal === 'ARCHIVE'}
          bgcolor="linear-gradient(0deg, rgba(124, 174, 190, 0.2), rgba(124, 174, 190, 0.2))"
          handleSuccess={handleSuccess}
          handleDismiss={handleDismiss}
          firstName={row.firstName}
          lastName={row.lastName}
          email={row.email}
          profilePicture={row.profilePic}
          icon="/assets/icons/archive-down.svg"
          title={t('coachesTable.archiveCoach.title')}
          subTitle={t('coachesTable.archiveCoach.subtitle')}
          btnText={t('coachesTable.archiveCoach.btnText')}
        />
      )}
      {showModal === 'EDIT' && (
        <AddTeamMemberModal
          isOpen={showModal === 'EDIT'}
          onClose={handleDismiss}
          isEdit
          initialData={editMemberData}
        />
      )}
    </Box>
  );
}
